import { isRef, onBeforeUnmount, ref, unref } from 'vue'
import { createClientRequestId, getReportOptimisticVersion } from '../workReportUtils'
import { createWorkReportDraftStateMachine } from './workReportDraftStateMachine.mjs'

function isVersionConflict(error) {
  return error?.response?.status === 409
    || error?.status === 409
    || error?.code === 'VERSION_CONFLICT'
    || error?.response?.data?.code === 'VERSION_CONFLICT'
}

function refreshReport(report, savedReport) {
  if (!savedReport || typeof savedReport !== 'object' || Array.isArray(savedReport)) {
    return
  }

  if (isRef(report)) {
    if (report.value && typeof report.value === 'object') {
      Object.assign(report.value, savedReport)
    } else {
      report.value = savedReport
    }
    return
  }

  if (report && typeof report === 'object') {
    Object.assign(report, savedReport)
  }
}

/**
 * Manage debounced work-report draft saves with optimistic locking.
 * The save callback receives a payload containing expectedVersion and clientRequestId.
 * @param {Object} options - Draft state options.
 * @param {Object|import('vue').Ref<Object>} options.report - Current work report.
 * @param {(payload: Object) => Promise<Object>} options.save - Save callback.
 * @param {(error: Error) => void|Promise<void>} [options.onConflict] - Conflict callback.
 * @param {(result: Object) => void|Promise<void>} [options.onSaved] - Save success callback.
 * @returns {Object} Draft state and controls.
 */
export function useWorkReportDraft({ report, save, onConflict, onSaved } = {}) {
  const hasUnsavedChanges = ref(false)
  const saveStatus = ref('idle')
  const draftState = createWorkReportDraftStateMachine({
    getExpectedVersion: () => getReportOptimisticVersion(unref(report)),
    createClientRequestId,
    save,
    isVersionConflict,
    onConflict,
    onSaved: async (savedReport) => {
      refreshReport(report, savedReport)

      if (typeof onSaved === 'function') {
        await onSaved(savedReport)
      }
    },
    onStateChange: (state) => {
      hasUnsavedChanges.value = state.hasUnsavedChanges
      if (state.saveStatus === 'conflict') {
        saveStatus.value = 'conflict'
        return
      }
      saveStatus.value = state.saveStatus
    }
  })

  onBeforeUnmount(() => {
    draftState.dispose()
  })

  return {
    markDirty: draftState.markDirty,
    flush: draftState.flush,
    retry: draftState.retry,
    cancelAutoSave: draftState.cancelAutoSave,
    discard: draftState.discard,
    dispose: draftState.dispose,
    hasUnsavedChanges,
    saveStatus
  }
}
